import type { PurchasePackage } from "@/types";

export type BillingPeriod = "weekly" | "monthly" | "annual";

// Single source of truth for classifying a package's billing period from its
// identifier. Shared by the paywall plan cards (ordering, labels, badges),
// the discount intercept, and the savings math below — a new SKU naming
// scheme only ever needs handling here.
export const getBillingPeriodFromIdentifier = (
  identifier?: string,
): BillingPeriod | undefined => {
  const normalizedIdentifier = identifier?.toLowerCase();

  if (!normalizedIdentifier) return undefined;
  if (
    normalizedIdentifier.includes("annual") ||
    normalizedIdentifier.includes("year")
  ) {
    return "annual";
  }
  if (
    normalizedIdentifier.includes("monthly") ||
    normalizedIdentifier.includes("month")
  ) {
    return "monthly";
  }
  if (
    normalizedIdentifier.includes("weekly") ||
    normalizedIdentifier.includes("week")
  ) {
    return "weekly";
  }

  return undefined;
};

export const getPackageBillingPeriod = (
  pkg?: PurchasePackage,
): BillingPeriod | undefined => getBillingPeriodFromIdentifier(pkg?.identifier);

export type PackagePricePresentation = {
  priceString: string;
  billingPeriod: BillingPeriod;
};

// Yearly is easiest to compare as a weekly equivalent, while its card subtitle
// still states the full annual charge. If a store ever omits its preformatted
// weekly price, keep the honest annual total + annual period instead of
// accidentally labeling a full-year charge as "/week".
export const getPackagePricePresentation = (
  pkg: PurchasePackage,
): PackagePricePresentation => {
  const billingPeriod = getPackageBillingPeriod(pkg) ?? "monthly";
  if (billingPeriod === "annual" && pkg.product.pricePerWeekString) {
    return {
      priceString: pkg.product.pricePerWeekString,
      billingPeriod: "weekly",
    };
  }
  return { priceString: pkg.product.priceString, billingPeriod };
};

// Effective per-week rate for a package, from its numeric price. Returns
// null when the price or billing period can't be resolved.
export const getWeeklyRate = (pkg?: PurchasePackage): number | null => {
  if (!pkg) return null;
  const price = (pkg.product as { price?: number }).price;
  if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) {
    return null;
  }
  switch (getPackageBillingPeriod(pkg)) {
    case "annual":
      return price / 52;
    case "monthly":
      return price / 4.33;
    case "weekly":
      return price;
    default:
      return null;
  }
};

// Rounded % saved by the yearly plan vs a short-term plan's effective rate.
// Null when either price is missing or the result isn't a sane discount —
// callers fall back to the generic "BEST OFFER" label. Shared between the
// every paywall plan-card badge so yearly savings stay consistent.
export const getYearlySavingsPct = (
  yearlyPkg?: PurchasePackage,
  shortTermPkg?: PurchasePackage,
): number | null => {
  const yearlyRate = getWeeklyRate(yearlyPkg);
  const shortRate = getWeeklyRate(shortTermPkg);
  if (yearlyRate === null || shortRate === null || shortRate <= yearlyRate) {
    return null;
  }
  const pct = Math.round((1 - yearlyRate / shortRate) * 100);
  return pct >= 20 && pct < 100 ? pct : null;
};
